/**
 * The instruction set handed to a routine.
 *
 * This is generated from the same constants the API validates against, and it
 * *is* the dispatch payload's top-level `prompt`, so what an agent is told and
 * what the server accepts cannot drift apart. If a status is added here it is
 * because it exists in `domain.ts`.
 *
 * The tone is deliberate: a routine is a language model reading a prompt, not a
 * client library reading a spec, so this says what to do and when rather than
 * enumerating a schema and hoping.
 *
 * Two shapes of the same content, for two different readers:
 *
 *   `protocolSection`  the full rules, every paragraph explained. Nobody has to
 *                       act on this directly — it is published at `/protocol`
 *                       and rendered by `/agent/instructions`, for a person or a
 *                       routine reading on its own terms.
 *   `protocolPointer`   what a dispatch actually carries: the concrete callback
 *                       for *this* task and a link to the page above for the
 *                       rules. Copying the whole document into every prompt was
 *                       the thing worth stopping — the callback is the only part
 *                       that cannot be looked up afterwards.
 */

import {
  AGENT_REPORTABLE_STATES,
  FIRE_TEXT_LIMIT,
  HEARTBEAT_STALE_MS,
  IMAGES_MAX_COUNT,
  LINK_LABEL_MAX_LENGTH,
  QUIET_WINDOW_MAX_MINUTES,
  STATUS_NOTE_MAX_LENGTH,
  DESIGN_PASSES_PER_TASK,
  FOLLOW_UPS_PER_TASK_MAX,
  QUESTION_OPTIONS_MAX,
  QUESTIONS_PER_BATCH_MAX,
  REVIEW_MANDATE,
  type Agent,
  type Project,
  type Repo,
  type Task,
} from './domain.ts';

export interface CallbackContract {
  report_url: string;
  poll_url: string;
  comment_url: string;
  upload_url: string;
  token: string;
  auth_header: string;
}

export function callbackContract(task: Task, functionsUrl: string): CallbackContract & { token: string } {
  const base = `${functionsUrl}/agent/tasks/${task.id}`;
  return {
    report_url: `${base}/report`,
    poll_url: base,
    comment_url: `${base}/comments`,
    upload_url: `${functionsUrl}/agent/uploads`,
    token: '',
    auth_header: 'Authorization: Bearer <token>',
  };
}

export interface BriefContext {
  task: Task;
  project: Project;
  repo?: Repo | null;
  agent?: Agent | null;
  category?: string | null;
  functionsUrl: string;
  appUrl: string;
  /** Plaintext callback token. Only available at mint time. */
  token: string;
  /** This project's own standing instructions, appended after the protocol. */
  instructions?: string | null;
  /**
   * How the last design pass on this task ended, when one has.
   *
   * On success the pass's own handover, verbatim — it names the batch it
   * committed and what it changed. Carried rather than summarised: the routine
   * is about to build to that design, and a paraphrase is exactly where the
   * detail it needs goes missing. On failure, the reason, because the routine
   * that asked for the pass is the one that has to decide what now.
   */
  designOutcome?: DesignOutcome | null;
}

export interface DesignOutcome {
  ok: boolean;
  /** What the routine asked the pass for. */
  asked: string | null;
  /** The pass's closing comments, when it finished. */
  notes: string | null;
  /** Why it did not, when it did not. */
  error: string | null;
}

/**
 * The protocol section, shared by the per-task brief and the standalone
 * documentation page. `token` is a placeholder in the docs and the real thing
 * in a dispatch.
 */
export function protocolSection(options: {
  functionsUrl: string;
  taskId: string;
  token: string;
  autoMerge: boolean;
}): string {
  const { functionsUrl, taskId, token } = options;
  const base = `${functionsUrl}/agent/tasks/${taskId}`;

  return `## Reporting protocol — required

You are running under Rigg, a control plane that shows a human what you are
doing. Nobody can see your session; the only thing they see is what you report.
Report by POSTing JSON to the callback URL below. The token is scoped to this
task alone.

    POST ${base}/report
    Authorization: Bearer ${token}
    Content-Type: application/json

Every field is optional — send what you know. Re-sending the same values is
always safe, so this doubles as a heartbeat.

    {
      "state":       "working",
      "activity":    "running the visual regression suite",
      "quiet_for":   30,
      "status_note": "where this stands, in prose — replaced every time",
      "links":       [{ "url": "https://…", "label": "Mobile preview", "kind": "preview" }],
      "images":      [{ "url": "https://…/shot.png", "label": "Settings, dark mode" }],
      "branch":      "agent/dark-mode",
      "pr_url":      "https://github.com/…/pull/412",
      "status":      "working",
      "message":     "one line for the activity log",
      "risk":        { "level": 1, "ux_impact": "none", "lines_changed": 12, … },
      "usage":       { "status": "allowed", "rateLimitType": "five_hour", "resetsAt": 1790944800 }
    }

### state — say what you are doing, every time it changes

This is the field that matters most and the one to send most often. It is how
the board knows whether anybody is actually on this task.

    working           you are on it. Send this the moment you start.
    ready_for_review  you have built your part and are handing it over.
    waiting_on_you    you are stuck and cannot go further without a human:
                      a decision, a credential, an answer to a question.
    blocked           you could not finish. Say why in status_note.
    done              your part is over.

The two handovers are not the same and Rigg treats them differently, so pick
the right one. \`waiting_on_you\` is a **question**: somebody can answer it from
the task itself, so it goes in front of a person immediately. \`ready_for_review\`
is an **invitation**: it asks them to go and look at something, which they
cannot do until there is something to look at.

So a \`ready_for_review\` with no \`preview\` link is held back until one arrives —
you have not been ignored, you have been believed. Rigg is not going to summon
somebody to a task whose preview is still deploying and whose checks are still
red, because the only thing they could do there is close it again. If no
preview ever turns up, it surfaces anyway: work that is finished never goes
quiet just because it has nothing to show.

The practical consequence: if you need an **answer**, say \`waiting_on_you\` and
put the question in \`status_note\`. Do not say \`ready_for_review\` and hope
somebody asks you what you meant.

**Post \`state\` with an \`activity\` line every time you move to a new phase of the
work** — starting, reading the code, writing it, running tests, pushing, taking
screenshots. \`activity\` is one short present-tense phrase and it appears live on
the board: "reading the router", "running the test suite", "waiting for the
preview build". If more than ${Math.round(HEARTBEAT_STALE_MS / 60_000)} minutes pass with no report, the task is
shown as stalled, so send one even when nothing has changed.

**When you need an answer from a human, set \`state\` to \`waiting_on_you\`** and
put the question in \`status_note\`. That is what puts the task in their *Needs
you* queue, straight away and whatever else is going on. Do not go quiet and
hope somebody notices.

### quiet_for — when you know you are about to disappear

Some work gives you no turn in which to speak: a long build, a full suite, a
skill that runs for half an hour without returning. Silence like that is not a
sign anything is wrong, but Rigg cannot tell it apart from a session that died
unless you say so first.

So say so first. \`quiet_for\` is the number of minutes you expect to be
unreachable, booked **before** you start the thing:

    {"state":"working","activity":"running the design-impl batch","quiet_for":40}

Silence inside that window is expected and the task stays live. The window is
spent the moment you report again — coming back is the all-clear — so book a
new one before each long phase rather than once at the start.

This buys nothing for free. It is a promise to be back by a stated moment, and
it is checked: miss the deadline and you are marked stalled at the time *you*
chose, with no further grace. Ask for what the phase actually needs, round up a
little, and report the moment you surface. The ceiling is ${QUIET_WINDOW_MAX_MINUTES} minutes; ask for
more and it is quietly cut to that, so a phase genuinely longer than that has to
come up for air in the middle.

### links — labelled, short, and there can be several

Send the whole set each time; it replaces what was there — except the session
link, which stays until you send a new one. Kinds:

    preview  something a reviewer can open and try. Several is normal:
             "Desktop preview", "Mobile preview", "Storybook".
    session  your Claude Code session URL, so they can watch you work.
             MANDATORY: send it with your first report. Until the task has
             one, Rigg refuses your reports (except blocked and
             waiting_on_you) with a 400 that says so.
    deploy   where it went live. On a merged task this marks it deployed.
    pr       the pull request you opened. Its checks are read from GitHub.
    other    a build log, a dashboard, anything else worth a click.

**If the work needed more than one pull request, report all of them** —
\`links: [{url: "…", kind: "pr"}, {url: "…", kind: "pr"}]\`, or a \`pr\` link in
each of several reports — rather than only the last one. Rigg tracks every
pull request it is told about and considers the task closed once every one
of them has merged, not just the first.

**Whenever your platform hands you a live preview for this PR — a running dev
server, a deploy preview, a sandbox link — report it as \`preview\` the moment it
exists.** Do not wait for a human to go find it on GitHub themselves, and do not
sit on it until you are finished; it is the single highest-value thing you can
hand over, so send it as soon as it is real.

These are not one-time links. Sending the same URL again on a later report is
expected, not wasteful — nobody "uses up" a preview by opening it. Whatever
serves it should mint a fresh session on each visit, or resume one already open
in the reviewer's browser, so the same link keeps working for as many looks as
it takes; do not treat it as spent after the first open, and do not rotate it
between reports unless the underlying URL actually changed.

Label them for a person, not for yourself. "Preview" three times tells them
nothing — but a label sits on a small button, so keep it to
**${LINK_LABEL_MAX_LENGTH} characters**, about as long as "Preview desktop"
("Mobile preview", "Storybook", not "Preview of the mobile layout on staging").
Go over and the whole report is rejected with a 400 that names the offending
link and says exactly how far over it is — trim that label and resend; nothing
else in the report is lost.

**If a preview needs signing in, the link you give should already do that.**
A bare app URL that dead-ends at a login screen is not a preview, it is a
locked door — and a person clicking it has no way to tell whether it is broken
or just needs a password they don't have. Report the already-authenticated URL
as the \`preview\` link itself, and report it here even if you have already put
it somewhere else — a pull request comment, a build log. This is the one place
a human is actually looking.

**The \`preview\` link is what makes a review possible**, so send it as soon as
anything is viewable rather than when you are finished — an early link is how
somebody tells you that you built the wrong thing while it is still cheap to
hear. Sending one does not interrupt anybody on its own; announcing
\`ready_for_review\` is what does that, and it only lands once a link exists.

### images — screenshots, labelled

Screenshots are the fastest way for someone to tell you that you built the wrong
thing, so take them early and take several. They appear as thumbnails on the
task and full size in the detail panel.

If you have nowhere to host an image, ask Rigg for somewhere:

    POST ${functionsUrl}/agent/uploads
    Authorization: Bearer ${token}
    { "filename": "settings-dark.png", "content_type": "image/png" }

    → { "upload_url": "…", "public_url": "…" }

PUT the bytes to \`upload_url\`, then report \`public_url\` in \`images\`.

**\`images\` replaces; \`add_images\` adds.** Reporting \`images\` sets the whole set,
so a second report carrying three shots leaves the task with three — which is
the right behaviour when you know the full set and the wrong one when you are
coming back with more:

    { "add_images": [{ "url": "…/state-error.png", "label": "Feil" }] }

Use \`add_images\` whenever you are adding to what is already there. A URL that
is already on the task is ignored rather than duplicated, so an overlapping
batch is safe to send. Between them the ceiling is
${IMAGES_MAX_COUNT} screenshots, and a report that would go over is
rejected with a 400 saying how many are already there — nothing is silently
dropped.

### status_note — keep this current

The field a person reads first, so write it for them, not for a log. Replace it
whenever the situation changes. Say what state the work is in and what to look
at:

    "I added the dark-mode toggle under Settings > Appearance. Open the desktop
     preview and switch it on — the whole app should re-theme immediately. I
     could not verify the print stylesheet."

Plain sentences. Say what you were unsure about or could not test. If you report
\`blocked\`, this is where you explain what went wrong and what you need.

**Limit: ${STATUS_NOTE_MAX_LENGTH} characters.** It is read at a glance, not
scrolled. Go over and the whole report is rejected with a 400 that says exactly
how many characters you sent and what the limit is — trim status_note and
resend; nothing else in the report is lost.

### risk — the measured size of the change

Once the change exists, measure it and report what you found. This is the
post-work measurement, so it beats any estimate made before you started — and
a later report replaces an earlier one whole, so send it again if a follow-up
push changes the answer.

    {
      "risk": {
        "level":         1,
        "ux_impact":     "cosmetic",
        "lines_changed": 12,
        "issue_ref":     482,
        "issue_gist":    "Footer privacy link 404s",
        "fix_gist":      "Point it at /privacy"
      }
    }

    level          1  contained — docs, copy, a small fix away from anything sensitive
                   2  ordinary  — a normal change a person should look at
                   3  sensitive — migrations, auth, sends, infra, CI, agent prompts
    ux_impact      none | cosmetic | minimal | moderate | major
    lines_changed  added + removed, as an integer
    issue_ref      the issue number this fixes, or null
    issue_gist     one line: what was wrong, or null
    fix_gist       one line: what you changed, or null

Every key is required, nothing else is accepted, and each value has to come
from its own set — a wrong shape gets the whole report rejected with a 400 that
names the key. It is what a person reads, and sorts by, when deciding whether
to approve without opening the diff, so report what you measured, not what you
hoped. If there is nothing to measure — no change, or not yet — leave it out;
Rigg shows nothing rather than a made-up value.

### usage — how much of your subscription is left

Rigg sends each task to the routine with the most room on its Claude
subscription, and the only way it knows is that you tell it. Your session's
own record carries the reading: call \`get_session\` with no \`session_id\` and
copy \`external_metadata.rate_limit_info\` into \`usage\` verbatim.

    {"usage": {"status":"allowed_warning","rateLimitType":"seven_day","resetsAt":1790944800}}

    status      allowed          fine
                allowed_warning  about to run out — this is the one to catch
                rejected         the limit has been hit
    rateLimitType   which window: five_hour, seven_day, …
    resetsAt        when it rolls over, unix seconds
    utilization     0–1, if you can see a figure (optional)
    isUsingOverage  true if you are spending extra usage (optional)

Send it with your first report and again after any heavy stretch of work; one
window per object, or an array of them. If \`status\` is anything but
\`allowed\`, say so in \`status_note\` too, with the reset time, so a person
sees it on the task as well. No such tool in your session? Leave it out — you
are then treated as having room.

### status — the lifecycle, moved rarely

Most reports never touch this. Send it when the work genuinely reaches a new
stage:

    working     the default while you are active
    signed_off  a human told you in chat that the work looks right
    merged      the change landed on the base branch
    deployed    it is live. Terminal — nothing is accepted afterwards.
    error       you could not finish

Reporting \`approved\` is recorded as a **sign-off**, not an approval. You cannot
verify who spoke to you, let alone that they were an admin, so an admin still
confirms the merge in Rigg. Do not merge on a chat message alone.

### Waiting for approval, and noticing a send-back

    GET ${base}
    Authorization: Bearer ${token}

    → { "approved": false, "signed_off": true, "reviewable": true,
        "sent_back_at": "2026-08-06T09:12:44Z", "unread_comments": 2, … }

${
  options.autoMerge
    ? `**Auto-merge is on for this task.** \`approved\` is already true: nobody is
waiting to review it. Finish the work, get the checks green, report
\`reviewed_sha\` (below), and Rigg lands it. If you are unsure whether the
change is safe, report \`blocked\` and explain rather than letting it merge.`
    : `Read this once when you need to know where the task stands. It is a state
check, not something to sit on: approval is announced as a comment on the pull
request when it happens, so you will be told rather than having to look.

An admin can approve while you are still working. That does not mean "merge
now": it switches auto-merge on and leaves the task in \`working\`, so
\`approved\` turns true and \`auto_merge\` with it. Carry on with the work
exactly as before, report \`reviewed_sha\` when it is done, and Rigg lands it.
Do not merge it yourself.`
}

### Landing it

${REVIEW_MANDATE}

    POST ${base}/report
    { "reviewed_sha": "<the head sha your code review covered>" }

**You do not have to merge, and you should not wait around to.** Once a pull
request is approved, Rigg merges it itself the moment three things are true: it
is clean against the base, its checks are green, and \`reviewed_sha\` matches the
head. That check runs every five minutes, so reporting the sha is the last thing
you need to do — the merge happens whether or not your session is still alive.

Report it as soon as your review is done, and report it **again** after any push
that follows, since the head has moved past what you read. Without it Rigg will
not merge: an unreviewed head is the one thing it refuses to land, and it will
comment on the pull request asking for the review rather than guessing.

What Rigg will not do is the work. A conflict, a red build, a review that has
not happened — those come back to you as a comment on the pull request, because
each needs a checkout and a judgement.

**Do not poll and do not wait.** Do the work, report it, and let the run end. A
loop that waits for a human spends a turn per check to learn nothing, and holds
open a session that is reclaimed on idle anyway. When you need a person, report
\`waiting_on_you\` with a \`status_note\` naming what you need, and stop. That is
a state somebody can see and act on.

**People reach you on the pull request.** Rigg posts comments there, which is
why the pull request matters: it is the one channel that still works once this
run is over. Your session cannot be woken from Rigg, but Claude Code watches the
pull requests you open, so a reply lands with you as a new turn.

So open it early — as a draft, as soon as you have a first commit worth pushing,
rather than saving it for when the work is presentable. It is a mailbox before it
is a proposal, and until it exists nobody can reach you. Anything said before
then is held and posted the moment it opens, so nothing is lost either way — but
held is slower than delivered.

\`sent_back_at\` is how you notice that a human has asked for another pass.
Compare it to the value you last saw. Do **not** try to infer this from
\`status\`, which stays \`working\` the whole time you are active.

### Talking back

    POST ${base}/comments
    Authorization: Bearer ${token}
    { "body": "The mobile layout needs a decision: stack or scroll?" }

This is how you speak to a person, and it is one-way: Rigg does not read the
pull request, so answer here rather than replying on GitHub. Use it for a
question, and pair it with \`waiting_on_you\` so somebody is actually asked to
look — a comment on its own is a message in a box nobody has been told to open.

### Asking questions

    POST ${base}/questions
    Authorization: Bearer ${token}
    { "questions": [
        { "kind": "choice",
          "question": "Should the export include archived tasks?",
          "context": "The board hides them, but the old CSV export included them.",
          "options": ["Include them", "Leave them out"] },
        { "kind": "text",
          "question": "What should the button say?" } ] }

Better than a question in \`status_note\` whenever the answer is one of a few:
the person picks instead of typing. \`choice\` offers \`options\` (2–${QUESTION_OPTIONS_MAX}) plus
an "Other" box for an answer you did not list — send \`"allow_other": false\`
only when the list is truly exhaustive. \`text\` is a free-text answer. Up to
${QUESTIONS_PER_BATCH_MAX} in one batch; a new batch replaces any still unanswered.

**Ask only what you genuinely cannot decide yourself.** If there is a sensible
default, take it and say so in \`status_note\` instead. Write each question in
plain English for someone who has not read the code: one short sentence, and
\`context\` saying in a line or two what you found and what hangs on the
answer.

**Open a pull request first — a draft is enough.** The pull request is the
only channel an answer can reach you on: the questions are posted to it, the
task moves to *Needs you*, and the answers come back as **one comment on the
pull request**, all at once. That comment is what wakes your session once
this run has ended. Ask with no pull request open and the questions still
reach the person, but their answers have nowhere to go — nothing will wake
you, and a \`GET\` of the task URL (where they also appear, in
\`questions\`) is only read by a run that is still going. So push a commit,
open the draft, then ask. Carry on with anything that does not depend on the
answers; otherwise end your run.

### Suggesting follow-ups

    POST ${base}/follow-ups
    Authorization: Bearer ${token}
    { "follow_ups": [
        { "title": "Remove the legacy CSV exporter",
          "prompt": "<a brief another routine can work from>",
          "auto_merge": false, "smart": false, "kind": "chore" } ] }

Work you noticed that is not this task's — a cleanup you kept out of the diff,
a bug next door. Do not widen this change to do it. Each follow-up is held until
this task merges, then offered to a person as a suggested task they can accept,
edit or reject. Write \`prompt\` as a complete brief: whoever picks it up has not
seen this task. \`kind\` — \`bug\`, \`enhancement\`, \`feature\` or \`chore\` — is
optional and only a suggestion: the person accepting it decides. At most ${FOLLOW_UPS_PER_TASK_MAX} per task.

### Asking for a design pass

    POST ${base}/design-passes
    Authorization: Bearer ${token}
    { "message": "<what to design, and what you already know>", "then": "resume" }

A design pass hands this branch to a design worker: it reads the code, designs
in a real design tool, renders and checks what it drew, and commits the result
to this branch. Ask for one when **the look is the hard part** — a new surface
whose layout is not obvious, or an existing one that is wrong in ways a code
change will not fix. It is slow (tens of minutes) and it is not free, so do not
ask for one on every change; nobody else will — this is your call.

\`message\` is required, and it is the pass's whole brief beyond the task
itself: which surfaces, which states, and what you have already found out.

\`then\` says what happens when the pass is over:

    resume    end your run now. When the pass finishes, Rigg sends the task
              to a routine again, briefed with what the pass committed — or
              why it failed. Use it to build to the design: ask before you
              start building, or after shooting a surface you are cleaning up.
    continue  keep working; the pass runs alongside you. Nothing is sent when
              it lands. Use it for a look over work you have finished.

Either way the pull request is held — not merged — until the pass is over.

**About to ask for a pass over a surface that already exists? Shoot it first.**
Every state it has — empty and with data, desktop and phone, light and dark —
reported as \`images\`. The pass reads the task's images before anything else,
and a state it never saw is one it will not design for.

**After asking with \`resume\`, stop.** Do not report again and do not wait: the
task is shown as waiting on the design pass, and that is true until you are
sent it again.

One pass at a time, and at most ${DESIGN_PASSES_PER_TASK} on one task. A 409 says why it was refused —
no design worker, a worker that is not running, a pass already in flight, or the
limit. Carry on without the design when you can; report \`waiting_on_you\` when
you cannot.

### The shape of a good run

    1. Immediately — the session link is mandatory:
                     {"state":"working","activity":"reading the code",
                      "links":[{"url":"<your session URL>","kind":"session"}],
                      "usage":<your session's rate_limit_info>}
    2. While working: a report at every phase change — activity is enough.
    3. About to be busy and silent for a while?  add "quiet_for": <minutes>
       to that report, before you start.
    4. As soon as anything is viewable: a "preview" link, images, a status_note.
       Do not wait until you are finished.
    5. Built and handing over?  {"state":"ready_for_review"} — with the preview
       link and your "risk" measurement, or it waits until the link arrives.
    6. Need an answer?  open a (draft) pull request, then POST /questions
                        — or {"state":"waiting_on_you",
                        "status_note":"<the question>"} for one that is not
                        a choice. Spotted more work?  POST /follow-ups.
    7. Reviewed?  {"reviewed_sha":"<head you reviewed>"} — then stop. Rigg
                  merges it once it is clean and green; you need not wait.
    8. Live?      {"status":"deployed","links":[{"url":"…","kind":"deploy"}]}

If you get stuck, report \`blocked\` with an explanation. Going silent is the one
genuinely unrecoverable failure: nobody can help with what they cannot see.`;
}

/**
 * The compact version — what a dispatch actually sends.
 *
 * `protocolSection` above is the canonical document; this is not a second copy
 * of it, it is a pointer to it plus the one thing the document cannot contain:
 * the real callback for this task. Everything else — why the two handovers
 * differ, what a label costs, how landing a review works — a routine reads once
 * at `${appUrl}/protocol` rather than re-reading on every dispatch.
 */
export function protocolPointer(options: {
  functionsUrl: string;
  appUrl: string;
  taskId: string;
  token: string;
  autoMerge: boolean;
}): string {
  const { functionsUrl, appUrl, taskId, token, autoMerge } = options;
  const base = `${functionsUrl}/agent/tasks/${taskId}`;
  const protocolUrl = `${appUrl}/protocol`;

  return `## Reporting protocol — required

You are running under Rigg, a control plane that shows a human what you are
doing. Nobody can see your session; the only thing they see is what you
report. Report by POSTing JSON to the callback below — every field is
optional, and re-sending the same values is always safe, so this doubles as a
heartbeat.

    POST ${base}/report
    Authorization: Bearer ${token}
    Content-Type: application/json

    {
      "state":       "working",
      "activity":    "running the visual regression suite",
      "status_note": "where this stands, in prose — replaced every time",
      "links":       [{ "url": "https://…", "label": "Mobile preview", "kind": "preview" }],
      "status":      "working"
    }

\`state\` is what matters most, sent every time it changes: working,
ready_for_review, waiting_on_you, blocked, done.

\`waiting_on_you\` is a **question** — put it in \`status_note\` and a human can
answer it from the task itself. \`ready_for_review\` is an **invitation** to
look at something, and is held back until one arrives: it needs a \`preview\`
link (kind: preview, session, deploy, pr or other) or it does not land in
front of anyone.

The \`preview\` link is what makes a review possible, so send it as soon as
anything is viewable — not one-time links, the same URL again on a later
report is expected — and report it as \`preview\` the moment it exists.

\`status_note\` is read first and capped at ${STATUS_NOTE_MAX_LENGTH} characters;
a link \`label\` is capped at ${LINK_LABEL_MAX_LENGTH} characters, about as long
as "Preview desktop". Either one over the limit gets the whole report
rejected with a 400 that says how far over you are — trim and resend.

Reporting \`{"status":"approved"}\` is recorded as a sign-off, not an approval;
only an admin confirms the merge. \`sent_back_at\` (from a \`GET\` on the task
URL above) is how you notice a human asked for another pass — compare it to
what you last saw, because \`status\` stays \`working\` the whole time and will
not tell you.

${
  autoMerge
    ? `**Auto-merge is on for this task.** Finish the work, get the checks green,
report \`reviewed_sha\` below, and Rigg lands it without a human in the loop.`
    : `Land it once it is reviewed:

    POST ${base}/report
    { "reviewed_sha": "<the head sha your review covered>" }

Rigg merges it itself once that sha matches the head, checks are green and the
branch is clean — you do not have to wait around for it.`
}

Measured the change? Send \`risk\` with the report that hands it over — the
latest one replaces any earlier, and it is what a person sorts approvals by:

    { "risk": { "level": 2, "ux_impact": "minimal", "lines_changed": 40,
                "issue_ref": 482, "issue_gist": "…", "fix_gist": "…" } }

\`level\` 1 contained, 2 ordinary, 3 sensitive; \`ux_impact\` none, cosmetic,
minimal, moderate or major; \`issue_ref\` and the two gists may be null. Every
key is required and no others are accepted — a wrong shape is a 400 naming the
key. Nothing to measure? Leave it out rather than guess.

Report your subscription's usage — it is how Rigg decides which routine gets
the next task. Call \`get_session\` (no \`session_id\`) and send its
\`external_metadata.rate_limit_info\` verbatim, with your first report and
after any heavy stretch:

    { "usage": { "status": "allowed", "rateLimitType": "five_hour", "resetsAt": 1790944800 } }

\`allowed_warning\` means about to run out, \`rejected\` means out — if you see
either, say so in \`status_note\` too. No such tool? Leave it out.

Talk back — this is one-way, so answer a question here rather than on the pull
request:

    POST ${base}/comments
    Authorization: Bearer ${token}
    { "body": "…" }

Need a decision? Open a draft PR (answers return on it), then ask:
\`POST ${base}/questions\`. Found work
beyond this task? Suggest it: \`POST ${base}/follow-ups\`. Both are
explained on the protocol page below.

A design pass is yours to ask for when the look is the hard part — nobody
else will. \`then: "resume"\` ends your run and Rigg sends the task back once
the pass is over; \`"continue"\` lets it run alongside you:

    POST ${base}/design-passes
    { "message": "<what to design>", "then": "resume" }

**Full rules, read once:** ${protocolUrl} — quiet_for, usage, uploads, questions,
follow-ups, the shape of a good run, and everything above explained rather than
asserted. This is the part specific to this task; that page is the same for
every one.`;
}

/** The complete, paste-ready brief a routine receives. */
export function buildBrief(context: BriefContext): string {
  const { task, project, repo, functionsUrl, appUrl, token } = context;
  const lines: string[] = [];

  lines.push(`# ${task.title}`);
  lines.push('');
  lines.push(
    `Task #${task.ref} in the **${project.name}** project` +
      (repo ? `, on \`${repo.owner}/${repo.name}\`` : '') +
      (context.category ? ` · ${context.category}` : '') +
      '.',
  );
  lines.push(`Rigg: ${appUrl}/t/${task.id}`);
  lines.push('');

  if (task.auto_merge) {
    lines.push(
      '> **Auto-merge is on.** Nobody is waiting to review this: get the work done and',
      '> the checks green, report `reviewed_sha`, and Rigg lands it without you.',
      '',
    );
  }

  lines.push('## What to do');
  lines.push('');
  lines.push(task.prompt.trim());
  lines.push('');

  // Before the protocol and after the ask, because it is neither: it is the
  // state of the world this routine is starting from. A routine resumed after a
  // pass it asked for is a fresh session — without this it gets the
  // description alone and rebuilds from scratch what is already on its branch,
  // or never learns that the design it is waiting for is not coming.
  const outcome = context.designOutcome;
  if (outcome?.ok) {
    lines.push('## A design pass has already run');
    lines.push('');
    if (outcome.asked?.trim()) {
      lines.push(`It was asked for: ${outcome.asked.trim()}`);
      lines.push('');
    }
    lines.push(
      'Its work is committed on ' +
        (task.branch ? `\`${task.branch}\`` : 'this task\'s branch') +
        ' — start from it rather than designing your own.' +
        (outcome.notes?.trim() ? ' What it said:' : ''),
    );
    lines.push('');
    if (outcome.notes?.trim()) {
      lines.push(outcome.notes.trim());
      lines.push('');
    }
  } else if (outcome) {
    lines.push('## The last design pass did not finish');
    lines.push('');
    if (outcome.asked?.trim()) {
      lines.push(`It was asked for: ${outcome.asked.trim()}`);
      lines.push('');
    }
    lines.push('How it ended:');
    lines.push('');
    lines.push(`    ${(outcome.error ?? 'no reason was given').split('\n').join('\n    ')}`);
    lines.push('');
    lines.push(
      'Whatever it committed before it stopped is on the branch. Decide what to do',
      'with that: build to what is there, ask for another pass if the failure looks',
      'passing, or report `waiting_on_you` if a person has to decide.',
    );
    lines.push('');
  }

  if (task.reopened_at) {
    lines.push(
      '> **This has been sent back.** Read the comments on the task before starting —',
      '> somebody has asked for changes to work you already did.',
      '',
    );
  }

  lines.push(
    protocolPointer({
      functionsUrl,
      appUrl,
      taskId: task.id,
      token,
      autoMerge: task.auto_merge,
    }),
  );

  // Last, deliberately. These are this project's own rules — which preview
  // links it wants, what to name a branch, who to mention — and they are the
  // part most likely to contradict the generic advice above. Whatever comes
  // last is what a reader treats as the final word, so that is where the
  // specific instruction belongs.
  if (context.instructions?.trim()) {
    lines.push('');
    lines.push(`## ${project.name}'s own instructions`);
    lines.push('');
    lines.push('These come from the people running this project. Where they and');
    lines.push('anything above disagree, these win.');
    lines.push('');
    lines.push(context.instructions.trim());
  }

  return lines.join('\n');
}

/** The JSON body POSTed to a routine's endpoint. */
/**
 * `FIRE_TEXT_LIMIT` (`domain.ts`) is the ceiling the routine trigger imposes;
 * `TASK_PROMPT_MAX_LENGTH` is derived from it so an accepted prompt always
 * fits. This stays as the backstop for the cases that arithmetic can't cover.
 *
 * Truncating loses the tail; a 400 loses the dispatch. Say which happened, so a
 * routine reading a brief that stops mid-sentence doesn't have to infer it.
 */
const TRUNCATION_NOTICE = '\n\n[brief truncated: too long for the routine trigger]';

function fitFireText(brief: string): string {
  if (brief.length <= FIRE_TEXT_LIMIT) return brief;
  return brief.slice(0, FIRE_TEXT_LIMIT - TRUNCATION_NOTICE.length) + TRUNCATION_NOTICE;
}

export function buildDispatchPayload(context: BriefContext) {
  const { task, project, repo, functionsUrl, token } = context;
  const base = `${functionsUrl}/agent/tasks/${task.id}`;
  const brief = buildBrief(context);

  return {
    version: '2',
    // Top-level so an endpoint that only understands `{ prompt }` needs no adapter.
    prompt: brief,
    // The same brief again under the name Claude Code's routine trigger reads.
    // That endpoint ignores every field but `text`, so without this it fires the
    // routine's saved prompt and never sees the task, the repo, or the callback
    // token. Endpoints that don't know the field ignore it, so this is not
    // conditional on which kind of endpoint is configured.
    //
    // Note for whoever wires one up: the trigger wraps this in a
    // <routine-fire-payload> block marked untrusted, so the routine's *saved*
    // prompt has to opt in to acting on it — see docs/agent-protocol.md.
    text: fitFireText(brief),
    task: {
      id: task.id,
      ref: task.ref,
      project_id: task.project_id,
      project: project.name,
      title: task.title,
      description: task.prompt,
      status: task.status,
      auto_merge: task.auto_merge,
      smart: task.smart,
      // A fresh dispatch has neither; a task dispatched after a design pass has
      // both, and they are the whole reason it is not starting from nothing.
      branch: task.branch,
      pr_url: task.pr_url,
      category: context.category ?? null,
      sent_back: Boolean(task.reopened_at),
      created_at: task.created_at,
    },
    repo: repo
      ? {
          owner: repo.owner,
          name: repo.name,
          full_name: `${repo.owner}/${repo.name}`,
          default_branch: repo.default_branch,
          clone_url: `https://github.com/${repo.owner}/${repo.name}.git`,
        }
      : null,
    callback: {
      report_url: `${base}/report`,
      poll_url: base,
      comment_url: `${base}/comments`,
      upload_url: `${functionsUrl}/agent/uploads`,
      token,
      auth_header: `Authorization: Bearer ${token}`,
      states: [...AGENT_REPORTABLE_STATES],
      statuses: ['working', 'signed_off', 'merged', 'deployed', 'error'],
      link_kinds: ['preview', 'session', 'deploy', 'pr', 'other'],
    },
  };
}

/**
 * What sent fire-and-forget work: an intake or a schedule, by name. Both hand
 * a prompt to a routine through the same path — see `dispatchFireAndForget`.
 */
export interface FireAndForgetSource {
  kind: 'intake' | 'schedule';
  /** The intake's name, or the schedule's title. */
  name: string;
}

/** How a source reads in a sentence: the “Hourly ops sweep” schedule. */
export function describeFireAndForgetSource(source: FireAndForgetSource): string {
  return `the "${source.name}" ${source.kind}`;
}

export interface FireAndForgetContext {
  source: FireAndForgetSource;
  title: string;
  prompt: string;
  project: Project;
  repo?: Repo | null;
  category?: string | null;
}

/**
 * What a fire-and-forget intake or schedule hands to a routine.
 *
 * No task, so none of `buildBrief`'s protocol section applies — there is no
 * id to poll, no token to report with, nothing on the board to send back to.
 * Saying that plainly matters: a routine that assumes every dispatch is a
 * task would otherwise go looking for a callback that does not exist.
 */
function buildFireAndForgetBrief(context: FireAndForgetContext): string {
  const { source, title, prompt, project, repo, category } = context;
  const lines: string[] = [];

  lines.push(`# ${title}`);
  lines.push('');
  lines.push(
    `Opened through ${describeFireAndForgetSource(source)} in the **${project.name}** project` +
      (repo ? `, on \`${repo.owner}/${repo.name}\`` : '') +
      (category ? ` · ${category}` : '') +
      '.',
  );
  lines.push('');
  lines.push(
    `> **Fire and forget.** This ${source.kind} does not open a task — nothing is on the`,
    '> board, there is no id to poll and no token to report with. Do the work and',
    '> stop; nobody is waiting on a callback.',
    '',
  );
  lines.push('## What to do');
  lines.push('');
  lines.push(prompt.trim());

  return lines.join('\n');
}

/** The JSON body POSTed to a routine's endpoint, for fire-and-forget work. */
export function buildFireAndForgetPayload(context: FireAndForgetContext) {
  const { repo } = context;
  const brief = buildFireAndForgetBrief(context);

  return {
    version: '2',
    prompt: brief,
    text: fitFireText(brief),
    task: null,
    repo: repo
      ? {
          owner: repo.owner,
          name: repo.name,
          full_name: `${repo.owner}/${repo.name}`,
          default_branch: repo.default_branch,
          clone_url: `https://github.com/${repo.owner}/${repo.name}.git`,
        }
      : null,
    callback: null,
  };
}

/**
 * The standing instructions for a routine that has an *agent* token rather than
 * a task token — the ones that let it open work nobody asked for.
 */
export function agentTokenInstructions(options: {
  functionsUrl: string;
  appUrl: string;
  token: string;
  agentName: string;
  projectName: string;
  categories: string[];
}): string {
  const { functionsUrl, appUrl, token, agentName, projectName } = options;
  const known = options.categories.length
    ? options.categories.map((c) => `"${c}"`).join(', ')
    : 'none yet';

  return `# Rigg — standing instructions for ${agentName}

You have a token that identifies you as **${agentName}** in the **${projectName}**
project. It works on every task assigned to you, and it lets you open new ones.

    Authorization: Bearer ${token}

## Opening a task nobody asked for

When you notice work that should happen — a stale lockfile, a failing nightly, a
migration nobody ran — do not just do it. Open a task first, so a human can see
it exists, and so what you do afterwards has somewhere to be reported.

This is also how you register work you are already doing — a Claude Code
session that started outside Rigg (on the web, say) and wants to make itself
visible rather than vanish into an unreported diff. Open the task with
\`claim: true\` before you do anything else; the callback token in the response
is what you report through from that point on, exactly as if Rigg had
dispatched you.

    POST ${functionsUrl}/agent/tasks
    Authorization: Bearer ${token}
    Content-Type: application/json

    {
      "title":    "Bump the lockfile — 4 advisories",
      "prompt":   "npm audit reports 4 moderate advisories …",
      "category": "Maintenance",
      "kind":     "chore",
      "repo":     "acme/site",
      "claim":    true,
      "auto_merge": false
    }

    → { "task": {…}, "callback": { "report_url": "…", "token": "…" } }

  * \`category\` is free text. If the project has not seen it before it is created,
    so "Maintenance", "Security" and "Flaky tests" all work. Categories in use
    here: ${known}.
  * \`claim: true\` means you are taking it yourself — the task is assigned to you
    and no other routine is dispatched. Use \`false\` to hand it to whoever is next
    in the rotation.
  * \`auto_merge: true\` waives the APPROVAL gate — nobody has to click approve
    before Rigg lands it. It does not waive the others: the pull request still
    has to be clean, its checks still have to be green, and its
    \`reviewed_sha\` still has to match the head. Only for work a person would
    wave through without looking.
  * \`smart: true\`, sent alongside \`claim: false\`, hands the task to whichever
    smart-intelligence routine is next in rotation instead of an ordinary one —
    see the Agents screen for what "smart" means here. No effect with
    \`claim: true\`, since nobody is being picked.
  * \`kind\` is optional: \`bug\`, \`enhancement\`, \`feature\` or \`chore\`. Where
    the task came from is recorded for you — it is yours, by name.

The response carries a task-scoped callback token. Use **that** token for
reports on the new task, and follow the same protocol as any other task: say
what you are doing, hand over a preview, keep \`status_note\` current.

## Which tasks are mine?

    GET ${functionsUrl}/agent/tasks
    Authorization: Bearer ${token}

Returns everything currently assigned to you, newest first, with the fields you
would get from polling each one — including \`sent_back_at\` and \`unread_comments\`,
so one call tells you where a human is waiting on you.

## Then report

Once you are working a task — opened here or dispatched to you — you have a
task-scoped callback token and the same reporting protocol as any other
routine applies: say what you are doing, hand over a preview, keep
\`status_note\` current. The full rules are at ${appUrl}/protocol.
`;
}

/* ── design passes ──────────────────────────────────────────────────────── */

export interface DesignBriefContext {
  task: Task;
  project: Project;
  repo?: Repo | null;
  functionsUrl: string;
  appUrl: string;
  /** Plaintext callback token for this pass. Only available at mint time. */
  token: string;
  /** The project's standing design instructions, if it has written any. */
  instructions?: string | null;
  /**
   * The worker's wall-clock ceiling on this pass, in minutes.
   *
   * Known only to the worker — it is a flag on the process — so it arrives with
   * the claim rather than being read from anywhere here. Absent when an older
   * worker claims, in which case the brief simply does not mention a clock.
   */
  budgetMinutes?: number | null;
}

/**
 * The brief for a design pass.
 *
 * Generated here rather than written into the worker, for the same reason every
 * other instruction set is: the worker should stay generic enough to serve any
 * project, and what a design pass *means* — where the design system lives, how
 * finished work is handed over — is a property of the project. That half is
 * `design_instructions`; this half is the part that is true everywhere.
 *
 * **The rule is: render, look, and go again.** The design tool's own prompt —
 * `get_claude_design_prompt`, 26 KB of it — is built around a verify loop:
 * render the file, screenshot it, gate on what is mechanically broken, then
 * re-read it with the asks in front of you and judge whether it is any good.
 * That loop is what a routine writing markup from a token file cannot do, and
 * the reason those come out generic.
 *
 * The surface is 23 tools and **none of them writes to the local disk**.
 * `write_files`'s `local_path` returns not-implemented and `copy_files` is
 * project-to-project, so getting a design into the repository is a client-side
 * loop over `read_file`. A brief that does not say so produces exactly what the
 * first real pass produced: 110 KB of correct work, in the right batch folder,
 * in the right language, inside a design project — and an empty branch.
 *
 * The prohibitions are not style. Each is a failure that consumed a real
 * 45-minute pass: a self-created project that orphaned every file written into
 * it, and a self-built Playwright rig, which was the run *obeying* that verify
 * loop on a host with no browser. So the fix for the second one is to provide a
 * browser, never to forbid the loop.
 */
export function buildDesignBrief(context: DesignBriefContext): string {
  const { task, project, repo, functionsUrl, appUrl, token, instructions, budgetMinutes } =
    context;
  const base = `${functionsUrl}/agent/tasks/${task.id}`;

  return `# Design pass · ${project.name} #${task.ref}

${task.title}

## What this is

A design pass, asked for by the routine working this task. You are **not**
building the feature and you are **not** reviewing the code — ${
    task.design_then === 'resume'
      ? `the routine has
stopped and is waiting for you: when you finish, it is sent this task again and
builds to whatever you committed. Put the design where it will find it.`
      : `the routine is doing
that, and it may still be working on this branch. You are producing the design
it should build to, and putting it somewhere it will find.`
  }

## What was asked for

${task.design_message?.trim() || 'The routine did not say — scope it from the task itself.'}
${
    task.images?.length
      ? `
## Read the screenshots first

The task carries ${task.images.length} screenshot${task.images.length === 1 ? '' : 's'} — what has been built, or the
surface as it stands. Poll

    GET ${base}
    Authorization: Bearer ${token}

and fetch every URL in \`images\`. They are the ground truth for what exists,
and between them they carry most of what the design system and the earlier
batches say, already applied.

**Then go and check the ones they cannot show you.** The design system and the
existing \`work-batch/\` folders hold decisions somebody already made about this
surface — spacing rules, a component that was chosen over another, a pattern
that was tried and rejected. A screenshot shows the outcome, never the reason,
so a pass that skips them re-decides settled questions and reads as churn.

Be **critical** of what you see. Say what is actually wrong — hierarchy,
density, rhythm, a state that was never designed — and fix that. If a surface is
already right, say so and leave it; the honest answer to "clean this up" is
sometimes "this one is fine".
`
      : ''
  }
${
    task.design_pass_count > 1
      ? `
## This is pass ${task.design_pass_count} — continue one, do not start one

An earlier pass has already run on this task, so there is work in the pinned
design project and possibly a commit on the branch. **Find it before you design
anything.**

1. \`list_files\` the pinned project and read what is already there for this
   task. The screenshots tell you what the surface looks like; this tells you
   what somebody already decided to do about it.
2. Check it against the task and against the repository: what is finished, what
   is half-drawn, what was never started. Verify rather than assume — an earlier
   pass that was cut off mid-render leaves artboards that look complete and are
   not.
3. **Continue that batch. Do not open a second one.** One task with two
   half-finished batches beside each other is worse than either alone, and
   nobody downstream can tell which is the real one.
${
  task.design_error
    ? `
How the last one ended, which tells you what to expect of what it left:

    ${task.design_error.split('\n').join('\n    ')}
`
    : ''
}
Say in your closing comment what you found already done and what you added, so
the pass after you does not have to work it out again.
`
      : ''
  }
## The task this is for

${task.prompt}

${repo ? `## Where\n\nRepository: ${repo.owner}/${repo.name}\nBranch: ${task.branch ?? `none yet — branch from ${repo.default_branch}`}\n${task.pr_url ? `Pull request: ${task.pr_url}\n` : ''}` : ''}
## How to run it

0. **Call \`get_claude_design_prompt\` first and follow it.** It is the design
   tool's own instructions, and above all its verify loop: render, look at the
   render, gate on what is mechanically broken, then look again with fresh eyes
   at whether it is any good. That loop is the entire reason this pass exists
   rather than a routine writing markup blind. **A browser is already installed
   on this machine for it — use it, and do not install one.**
1. Read the repository for context — the components this actually touches, the
   real copy, the states the surface can be in. This is the part the design tool
   cannot do: it cannot see the repo.
2. Design the surfaces and \`write_files\` them into the **pinned** project named
   in the instructions below.
3. \`render_preview\` each one and run the verify loop until it converges.
4. **Pull the batch back out and write it into the worktree.** \`list_files\`,
   then \`read_file\` each one, and write the bytes to disk yourself. **Nothing
   in the tool surface does this for you** — \`write_files\`'s \`local_path\` is
   not implemented and \`copy_files\` is project-to-project — so a pass that
   skips this has left its work in a design project and put nothing in the
   repository. That is exactly how the first real pass produced 110 KB of good
   design and an empty branch.
5. **Commit** what you wrote, touching only the handoff path named in the
   project instructions below. Do **not** push — the worker owns the branch and
   pushes everything you commit once you exit. A pass that commits nothing has
   produced nothing and is reported as a failure, so commit before you finish.
${
    budgetMinutes
      ? `
## You have ${budgetMinutes} minutes

A wall clock, not a warning. The worker stops the run at that point whatever you
are in the middle of, and three tiers of work survive it differently:

- **committed** — pushed to the branch, and the only thing that is properly
  yours.
- **written to the worktree but not committed** — the worker commits it for you
  rather than lose it, marked as a salvage, because it has not been through the
  verify loop.
- **left in the design project** — reaches nobody. \`write_files\` puts it
  somewhere the repository cannot see, and step 4 is the only thing that moves
  it.

So **commit as you go**: every time one surface converges, pull it out and
commit it, rather than saving a single commit for the end you may not reach. And
scope to the clock — a handful of surfaces designed properly and committed is
worth more than every surface designed and none of them landed. If you run out
of time, that is fine and expected; say what you covered in your closing
comment so the next pass starts where you stopped.

One thing to send with each report: **\`quiet_for\`, in minutes, for the phase you
are about to start.** A task is called stalled after ${Math.round(HEARTBEAT_STALE_MS / 60_000)} minutes of silence, and
a render-and-verify loop over several artboards is longer than that on its own —
so a pass that reports without booking the next window marks itself dead halfway
through, while it is working perfectly well. The worker booked one covering the
whole pass when it claimed it; your first report spends it, and every report
after that has to book the next.
`
      : ''
  }
**Three rules, each of which cost a real pass:**

- **Never call \`create_project\`.** The project is pinned in the instructions
  below and it is the one holding the design system. Creating your own is the
  path of least resistance when \`finalize_plan\` asks for a writable project,
  and it silently orphans everything you write into a project nobody will ever
  open.
- **Stay inside the worktree.** It is disposable and it is the only thing
  cleaned up after you. Anything you leave in \`/tmp\`, a cache directory or a
  home directory outlives the pass and is nobody's to remove.
- **Never write a \`serve_url\` anywhere it persists** — not into a file, not
  into a commit, not into a report or a comment. Its query token is
  project-scoped and needs no other credential, so it is the only thing standing
  between that URL and anyone who has it.

**If the design tool is unreachable, stop and report \`error\` saying exactly
that.** Do not quietly carry on without it — a pass that never rendered anything
is a routine writing markup blind, which is the thing this replaces.

## Reporting

    POST ${base}/report        state, activity, images, status_note
    POST ${base}/comments      one comment when you are done
    POST ${functionsUrl}/agent/uploads    a signed URL to put a screenshot
    Authorization: Bearer ${token}

Report \`state: "working"\` when you start and keep \`activity\` current — the
board cannot otherwise tell a running pass from a dead worker.

**Upload the rendered artboards as \`images\` before you finish.** They are the
only evidence anybody has that the design tool actually ran, and on most of
these tasks they are the only thing a person will look at.

**Your closing comment is the handover.** Rigg posts it onto the pull request,
which is what wakes the routine — so name the path you committed and the
commit sha, and say in one line what changed. Full protocol: ${appUrl}/protocol.
${instructions ? `\n## ${project.name}\n\n${instructions}\n` : ''}`;
}
